// Durchgänge 1 und 2 sind in snippets/hp-variant-marks.liquid gespiegelt (Aufruf: snippets/variant-picker.liquid). Regeländerung = JavaScript + Liquid + docs/tools/startvariante/marks_parity.js.
/**
 * Variant Availability Filter — HIG-35
 * ------------------------------------------------------------------
 * Marks option-2 (and option-3) values as "non-existing" when the
 * currently-selected option-1 has no variant that pairs with them.
 *
 * Example: Whey Protein has Geschmack (option-1) + Menge (option-2).
 * Most Mengen only exist for a subset of Geschmäcker. Selecting
 * "Extreme Milk Chocolate" should grey-out the Mengen that don't
 * have a matching variant.
 *
 * Directionality:
 *   Only option-2 / option-3 are filtered, NEVER option-1. Filtering
 *   option-1 by option-2 would hide colors when a single size limits
 *   the visible palette — that's the wrong UX (the user might want
 *   to re-pick size).
 *
 * Stacks with Shopify's `available` (OOS) flag:
 *   This module adds the class `is-non-existing` to inputs whose
 *   combination doesn't exist as a Shopify variant.
 *   product.css / swatches-styles.css render `.is-non-existing` with
 *   the same strike-through + opacity treatment as `.is-unavailable`
 *   (true sold-out). Two separate classes so the variant-picker.js
 *   OOS logic and this module don't fight over a single class.
 *
 * Data source:
 *   <variant-picker> emits a `<script type="application/json"
 *   data-all-variants>` with the full variant matrix (id, option1,
 *   option2, option3, available). Tiny payload, no extra fetch.
 *
 * Triggers:
 *   - Initial render (DOMContentLoaded + every <variant-picker>
 *     encountered).
 *   - Every change event on the picker (a variant-change fires the
 *     existing on:variant:change too, but we listen to the lower-
 *     level 'change' to also catch in-progress option-1 swaps
 *     before the variant-fetch resolves).
 *
 * Idempotent: refilter() is safe to run any number of times.
 */
(function () {
  // Guard: this file can be loaded both by variant-picker.liquid (PDP) and by
  // quick-add-drawer.liquid (global, so the observer also runs on pages whose
  // drawer opens a picker — collection/home/slider). Run the IIFE only once.
  if (window.__hpVariantAvailabilityInit) return;
  window.__hpVariantAvailabilityInit = true;

  function processPicker(picker) {
    const dataEl = picker.querySelector('script[type="application/json"][data-all-variants]');
    if (!dataEl) return;
    let variants;
    try { variants = JSON.parse(dataEl.textContent); } catch { return; }
    if (!Array.isArray(variants) || variants.length === 0) return;

    const selectors = picker.querySelectorAll('.option-selector:not(.option-selector--product-group)');
    if (selectors.length < 2) return; // single-option products: nothing to filter

    function selectedValueFor(selector) {
      const radio = selector.querySelector('.js-option:checked');
      if (radio) return radio.value;
      const dropdown = selector.querySelector('.custom-select__option[aria-selected="true"]');
      if (dropdown && dropdown.dataset.value !== '') return dropdown.dataset.value;
      return null;
    }

    // Tobias 2026-06-04: contextual sold-out BELL, BIDIRECTIONAL. For EVERY option value,
    // if no matching variant with the currently-selected OTHER options is available,
    // mark it `is-unavailable` (→ bell, clickable for STOQ notify). Tobias 2026-07-11:
    // a value with no contextual match is struck only when it is also globally sold out.
    // If every visible value in a row would be struck, progressively drop the last other
    // selected row first until the row carries a useful signal (or all values are globally
    // sold out). This keeps the bell informative instead of blaming a downstream row for
    // an impossible selection caused elsewhere. Hidden `is-non-existing` values stay
    // unmarked. Bidirectional is OK here because a bell informs but never hides a value.
    function markContextualSoldOut() {
      const sel = [];
      selectors.forEach((s) => sel.push(selectedValueFor(s)));

      function isContextSoldOut(idx, val, constraints) {
        let exists = false;
        for (const v of variants) {
          const opts = [v.option1, v.option2, v.option3];
          if (opts[idx] !== val) continue;
          let matchesOthers = true;
          for (const constraint of constraints) {
            if (opts[constraint.idx] !== constraint.value) { matchesOthers = false; break; }
          }
          if (!matchesOthers) continue;
          exists = true;
          if (v.available) return false; // an available combo exists → not sold out
        }
        if (exists) return true; // matching combos exist, but all are sold out
        return !variants.some((v) => {
          const opts = [v.option1, v.option2, v.option3];
          return opts[idx] === val && v.available;
        });
      }

      selectors.forEach((selector, idx) => {
        const values = [];
        selector.querySelectorAll('input.js-option').forEach((inp) => {
          values.push({ el: inp, value: inp.value });
        });
        selector.querySelectorAll('.custom-select__option').forEach((opt) => {
          if (opt.dataset.value !== '') values.push({ el: opt, value: opt.dataset.value });
        });

        let constraints = [];
        for (let j = 0; j < selectors.length; j++) {
          if (j !== idx && sel[j] != null) constraints.push({ idx: j, value: sel[j] });
        }

        // Tobias 2026-07-11 (C4-Feedback): relaxation only applies when the dead
        // selection is COMBINATION-caused (each constrained value is buyable somewhere,
        // only together they fail — e.g. Pack Schwarz+XL). If any constrained value is
        // globally sold out (e.g. flavour "Frozen Bombsicle": every variant OOS), the
        // whole subtree genuinely IS sold out and the row must keep its bells.
        // Tobias 2026-09-20: that block is ORDER-AWARE now — a globally dead value
        // strikes only DOWNSTREAM rows, never upstream ("ein toter Wert streicht nur
        // nach unten durch"). Only a dead value in an EARLIER row (c.idx < idx) blocks
        // relaxation; a dead LATER value (e.g. Springseil Blau komplett ausverkauft)
        // must not poison the grip rows above it — its constraint is simply the first
        // one the relaxation loop drops. C4 stays correct: for the Menge row the dead
        // flavour sits in an earlier row and keeps blocking.
        const isGloballyDead = (i, val) => !variants.some((v) => {
          const opts = [v.option1, v.option2, v.option3];
          return opts[i] === val && v.available;
        });
        const relaxable = !constraints.some((c) => c.idx < idx && isGloballyDead(c.idx, c.value));

        let states;
        do {
          states = values.map(({ value }) => isContextSoldOut(idx, value, constraints));
          const allVisibleUnavailable = values.every(({ el }, i) =>
            el.classList.contains('is-non-existing') || states[i]);
          if (!allVisibleUnavailable || !relaxable || constraints.length === 0) break;
          constraints = constraints.slice(0, -1);
        } while (true);

        values.forEach(({ el }, i) => {
          // A non-existing combo (handled separately) gets no bell.
          if (el.classList.contains('is-non-existing')) { el.classList.remove('is-unavailable'); return; }
          el.classList.toggle('is-unavailable', states[i]);
        });
      });
    }

    function refilter() {
      const v1 = selectedValueFor(selectors[0]);
      if (v1 == null) return;

      // Pass 1: is-non-existing — one-way cascade (option1→2→3), HIG-35 (downstream only,
      // so we never HIDE an upstream option).
      const validBySel = [];
      for (let i = 1; i < selectors.length; i++) {
        const sel = selectors[i];
        const valid = new Set();
        const v2 = i >= 2 ? selectedValueFor(selectors[1]) : null;
        for (const variant of variants) {
          if (variant.option1 !== v1) continue;
          if (i >= 2 && variant.option2 !== v2) continue;
          const k = i === 1 ? variant.option2 : variant.option3;
          if (k != null) valid.add(k);
        }
        validBySel[i] = valid;
        sel.querySelectorAll('input.js-option').forEach((inp) => {
          inp.classList.toggle('is-non-existing', !valid.has(inp.value));
        });
        sel.querySelectorAll('.custom-select__option').forEach((opt) => {
          if (opt.dataset.value === '') return;
          opt.classList.toggle('is-non-existing', !valid.has(opt.dataset.value));
        });
      }

      // Pass 2: contextual sold-out bells (is-unavailable), bidirectional.
      markContextualSoldOut();

      // Pass 3: auto-correct (Tobias 2026-06-01) — if the value still selected in a
      // downstream option no longer pairs with the upstream selection (combination
      // doesn't exist), jump to the first EXISTING + IN-STOCK value. A still-valid value
      // is kept (size stays when you click through colours). Prevents ?variant=undefined
      // dead-ends. (Full "pick best available variant" = HIG-60.)
      for (let i = 1; i < selectors.length; i++) {
        const v2 = i >= 2 ? selectedValueFor(selectors[1]) : null;
        if (autoCorrectSelector(selectors[i], i, v1, v2, validBySel[i])) return;
      }
    }

    function autoCorrectSelector(sel, i, v1, v2, valid) {
      if (picker.__hpAutoSelect) return false;
      const radio = sel.querySelector('input.js-option:checked');
      const dropdown = sel.querySelector('.custom-select__option[aria-selected="true"]');
      const currentVal = radio
        ? radio.value
        : (dropdown && dropdown.dataset.value !== '' ? dropdown.dataset.value : null);
      if (currentVal == null || valid.has(currentVal)) return false;

      const isAvailable = (val) => variants.some((v) =>
        v.option1 === v1
        && (i === 1 ? v.option2 === val : (v.option2 === v2 && v.option3 === val))
        && v.available);

      const candidates = [];
      if (radio) {
        sel.querySelectorAll('input.js-option').forEach((inp) => {
          if (valid.has(inp.value)) candidates.push(inp);
        });
      } else {
        sel.querySelectorAll('.custom-select__option').forEach((opt) => {
          if (opt.dataset.value !== '' && valid.has(opt.dataset.value)) candidates.push(opt);
        });
      }
      if (!candidates.length) return false;

      // first in-stock candidate (document order = first/smallest); else first valid
      const pick = candidates.find((el) => isAvailable(radio ? el.value : el.dataset.value))
        || candidates[0];

      picker.__hpAutoSelect = true;
      setTimeout(() => {
        try {
          if (radio) {
            const lbl = sel.querySelector(`label[for="${pick.id}"]`);
            (lbl || pick).click();
          } else {
            const cs = pick.closest('custom-select');
            if (cs && typeof cs.selectOption === 'function') cs.selectOption(pick);
            else pick.click();
          }
        } finally {
          setTimeout(() => { picker.__hpAutoSelect = false; }, 60);
        }
      }, 0);
      scheduleHealCheck(picker.closest('.js-quickbuy, .cc-featured-product, .shopify-section') || document.body);
      return true; // re-runs refilter via the triggered change; handle one option/pass
    }

    // Ein Klick (Auto-Correct ODER schneller Nutzer-Doppelklick) kann in das
    // 10-ms-Fenster fallen, in dem variant-picker.js nach einem Content-Swap
    // die Listener des frisch eingesetzten Pickers entfernt hat
    // (updateContent: removeListeners → addListeners erst per setTimeout(10)).
    // Der Klick ändert dann nur die Radio-Auswahl; kein Fetch, keine URL, kein
    // Button-Update — die Seite bleibt auf der vorherigen (oder einer nicht
    // existierenden) Variante hängen. Darum nach jedem Change verifizieren,
    // dass der aktuell im DOM stehende Picker die Auswahl verarbeitet hat
    // (vp.variant.id == Matrix-Treffer der Auswahl), und das change-Event
    // sonst erneut zustellen. Läuft die urspruengliche Verarbeitung nur
    // langsam (Fetch in flight), ist die erneute Zustellung harmlos:
    // fetchCache dedupliziert, variantRequestId behält die letzte Antwort.
    function scheduleHealCheck(scopeEl) {
      [350, 1400].forEach((delay) => {
        setTimeout(() => {
          const vp = scopeEl.querySelector('variant-picker');
          if (!vp) return;
          const dataEl = vp.querySelector('script[type="application/json"][data-all-variants]');
          if (!dataEl) return;
          let matrix;
          try { matrix = JSON.parse(dataEl.textContent); } catch { return; }
          if (!Array.isArray(matrix)) return;
          const sels = vp.querySelectorAll('.option-selector:not(.option-selector--product-group):not(.option-selector--custom)');
          const chosen = [];
          for (const s of sels) {
            const radio = s.querySelector('input.js-option:checked');
            const dd = s.querySelector('.custom-select__option[aria-selected="true"]');
            const val = radio ? radio.value : (dd && dd.dataset.value !== '' ? dd.dataset.value : null);
            if (val == null) return; // unvollständige Auswahl: kein Heilungsfall
            chosen.push(val);
          }
          const match = matrix.find((v) => chosen.every((val, i) => v[`option${i + 1}`] === val));
          if (!match) return; // Kombination existiert nicht: Auto-Correct-Pass übernimmt
          if (vp.variant && String(vp.variant.id) === String(match.id)) return; // konsistent
          const input = vp.querySelector('.option-selector input.js-option:checked')
            || vp.querySelector('.option-selector .custom-select__native');
          if (!input) return;
          // Stale data-variant-id ist unkritisch: variant-picker.js validiert die
          // Kandidaten-ID gegen die Matrix und fällt sonst auf option_values zurück.
          input.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { noFocus: true } }));
        }, delay);
      });
    }

    picker.addEventListener('change', () => {
      // Defer one tick so radio's :checked state has updated.
      setTimeout(refilter, 0);
      // Jeder Change kann der zweite eines schnellen Doppelklicks sein und im
      // tauben Listener-Fenster des frisch geswappten Pickers landen — darum
      // immer die Konvergenz Auswahl == geladene Variante nachprüfen.
      scheduleHealCheck(picker.closest('.js-quickbuy, .cc-featured-product, .shopify-section') || document.body);
    });
    refilter();
  }

  // Process a picker at most once (its element identity). The quick-add /
  // choose-options drawer injects a fresh <variant-picker> when it opens AND
  // variant-picker.js recreates the element on each variant change, so the
  // observer below sees a new element each time — each gets processed once.
  function processPickerOnce(picker) {
    if (picker.dataset.hpAvailabilityProcessed) return;
    picker.dataset.hpAvailabilityProcessed = '1';
    processPicker(picker);
  }

  function init() {
    document.querySelectorAll('variant-picker').forEach(processPickerOnce);
  }

  // Catch variant-pickers added after load — specifically the choose-options
  // drawer (HIG-43): without this, non-existing option combinations aren't
  // greyed out in the drawer the way they are on the PDP.
  function observe() {
    if (!('MutationObserver' in window)) return;
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('variant-picker')) processPickerOnce(node);
          if (node.querySelectorAll) node.querySelectorAll('variant-picker').forEach(processPickerOnce);
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { init(); observe(); });
  } else {
    init();
    observe();
  }
})();
